import { useEffect, useState } from 'react'
import { Button, Input, Modal, Select, Switch, useToast } from '@/components/ui'
import { useCreateTariff, useUpdateTariff } from '../api/contractsApi.ts'
import type { BillingCycle, Tariff } from '../types.ts'

interface TariffFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: string
  tariff?: Tariff | null
  onCreated?: (tariff: Tariff) => void
}

interface FormState {
  name: string
  billingCycle: BillingCycle
  validFrom: string
  validTo: string
  currency: string
  active: boolean
}

const CYCLE_OPTIONS: { value: BillingCycle; label: string }[] = [
  { value: 'monthly', label: 'Monatlich' },
  { value: 'quarterly', label: 'Quartalsweise' },
  { value: 'yearly', label: 'Jährlich' },
  { value: 'once', label: 'Einmalig' },
]

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function isoDate(d: string | null | undefined): string {
  if (!d) return ''
  return new Date(d).toISOString().slice(0, 10)
}

const EMPTY: FormState = {
  name: '',
  billingCycle: 'monthly',
  validFrom: todayIso(),
  validTo: '',
  currency: 'EUR',
  active: true,
}

export function TariffFormModal({ open, onOpenChange, productId, tariff, onCreated }: TariffFormModalProps) {
  const isEdit = Boolean(tariff)
  const { toast } = useToast()
  const create = useCreateTariff()
  const update = useUpdateTariff(tariff?.id ?? '')
  const [form, setForm] = useState<FormState>(EMPTY)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (tariff) {
      setForm({
        name: tariff.name,
        billingCycle: tariff.billingCycle,
        validFrom: isoDate(tariff.validFrom),
        validTo: isoDate(tariff.validTo),
        currency: tariff.currency,
        active: tariff.active,
      })
    } else {
      setForm(EMPTY)
    }
    setError(null)
  }, [open, tariff])

  function update_<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function handleSave() {
    if (!form.name.trim()) {
      setError('Name ist Pflicht.')
      return
    }
    if (!form.validFrom) {
      setError('Gültig ab ist Pflicht.')
      return
    }
    setError(null)

    const onError = (err: Error) => setError(err.message || 'Speichern fehlgeschlagen.')

    if (isEdit && tariff) {
      update.mutate(
        {
          name: form.name.trim(),
          billingCycle: form.billingCycle,
          validFrom: form.validFrom,
          validTo: form.validTo || null,
          currency: form.currency,
          active: form.active,
        },
        {
          onSuccess: (saved) => {
            toast({ title: 'Tarif aktualisiert', tone: 'success' })
            onCreated?.(saved)
          },
          onError,
        },
      )
    } else {
      create.mutate(
        {
          productId,
          name: form.name.trim(),
          billingCycle: form.billingCycle,
          validFrom: form.validFrom,
          validTo: form.validTo || null,
          currency: form.currency,
          active: form.active,
        },
        {
          onSuccess: (saved) => {
            toast({ title: 'Tarif angelegt', tone: 'success' })
            onCreated?.(saved)
          },
          onError,
        },
      )
    }
  }

  const isPending = create.isPending || update.isPending

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={isEdit ? 'Tarif bearbeiten' : 'Neuer Tarif'}
      description="Versionierter Preis-/Vertragsmodell-Header. Komponenten und Mengenstaffeln pflegst du im aufgeklappten Tarif."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={isPending}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
            {error}
          </div>
        ) : null}

        <Input
          label="Name"
          hint="z. B. „Managed Workplace Premium 2026"
          value={form.name}
          onChange={(e) => update_('name', e.currentTarget.value)}
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Select<BillingCycle>
            label="Abrechnungsintervall"
            value={form.billingCycle}
            onValueChange={(v) => update_('billingCycle', v)}
            options={CYCLE_OPTIONS}
          />
          <Input
            label="Währung"
            value={form.currency}
            onChange={(e) => update_('currency', e.currentTarget.value.toUpperCase().slice(0, 8))}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Gültig ab"
            type="date"
            value={form.validFrom}
            onChange={(e) => update_('validFrom', e.currentTarget.value)}
            required
          />
          <Input
            label="Gültig bis"
            hint="Leer = unbefristet"
            type="date"
            value={form.validTo}
            onChange={(e) => update_('validTo', e.currentTarget.value)}
          />
        </div>

        <Switch
          label="Aktiv"
          hint="Inaktive Tarife können nicht mehr neu auf Verträge gesetzt werden"
          checked={form.active}
          onChange={(e) => update_('active', e.currentTarget.checked)}
        />
      </div>
    </Modal>
  )
}
